<template>
  <div>
    <!-- From http://codepen.io/brycesnyder/pen/rtkwD -->
    <!-- ErrorPage displays standard JavaScript Errors neatly,
         by name and then message -->
    <!--
    Copyright (c) ???? - Bryce Snyder
    Copyright (c) 2017 - QReserve Inc - URL to original above

    Permission is hereby granted, free of charge, to any person
    obtaining a copy of this software and associated documentation
    files (the "Software"), to deal in the Software without restriction,
    including without limitation the rights to use, copy, modify,
    merge, publish, distribute, sublicense, and/or sell copies of
    the Software, and to permit persons to whom the Software is
    furnished to do so, subject to the following conditions:

    The above copyright notice and this permission notice shall
    be included in all copies or substantial portions of the Software.

    THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
    EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
    OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
    NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
    HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
    WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
    OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
    DEALINGS IN THE SOFTWARE.

    -->
    <h2 style="text-align: center">
      <span class="label label-danger round-corners">
        {{ err.name }}
      </span>
    </h2>
    <div class="window">
      <div class="button"></div>
      <div class="display">?</div>
    </div>
    <br />
    <div class="error-form">
      <p style="font-size: 1.6em">{{ err.message }}</p>
      <br />
      <p style="font-size: 1.2em">
        The QReserve application has encountered an error. You can send an error
        report or reload the application page below. The error report will
        contain information that can assist us in preventing this problem in the
        future, along with your message.
      </p>
      <br />
      <div class="form-group">
        <label class="form-label" for="message-to-devs">
          Message to Developers
        </label>
        <textarea
          class="form-input"
          id="message-to-devs"
          placeholder="Details (optional)"
          rows="3"
          style="margin-bottom: 6px"
          v-model="message"></textarea>
        <button
          class="btn btn-primary"
          @click="generateErrorReport"
          :class="{ loading }">
          Send Error Report and Reload
        </button>
        <button
          class="btn btn-secondary"
          style="float: right"
          @click="$emit('clearError')">
          Reload
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

import {
  AuthErrorString,
  AuthErrorTokenExpString,
  AuthErrorTokenExpUnknownString,
  Post,
  urls,
} from 'assets/js/api';

// stringifyError converts a JS error into a string encoded JSON object.
const stringifyError = (err, spaceArg) => {
  const filter = null;
  let space = '';
  if (spaceArg) space = spaceArg;
  const plainObject = {};
  Object.getOwnPropertyNames(err).forEach((key) => {
    plainObject[key] = err[key];
  });

  // Prune out the user token.
  if (_.get(plainObject, ['original', 'config', 'headers', 'Authorization'])) {
    delete plainObject.original.config.headers.Authorization;
  }
  if (
    _.get(plainObject, ['original', 'config', 'headers', 'VND-QReserve-Token'])
  ) {
    delete plainObject.original.config.headers['VND-QReserve-Token'];
  }

  return JSON.stringify(plainObject, filter, space);
};

export default {
  name: 'error-page',

  components: {},

  computed: {},

  created() {
    if (process.env.NODE_ENV === 'development') {
      console.debug(stringifyError(this.err, '\t'));
    }
  },

  data() {
    return {
      loading: false,
      message: '',
    };
  },

  emits: ['clearError'],

  methods: {
    // generateErrorReport creates an error report that supplies the
    // following information to QReserve:
    // - UTC Timestamp
    // - Logged in user
    // - Route
    // - Full error object
    async generateErrorReport() {
      this.loading = true;
      const ts = new Date().getTime();
      const user = this.$store.state.auth?.authUser;
      const route = {
        fullPath: this.$route.fullPath,
        hash: this.$route.hash,
        meta: this.$route.meta,
        name: this.$route.name,
        params: this.$route.params,
        path: this.$route.params,
        query: this.$route.query,
      };

      // Errors are wacky, so convert them to a string first.
      let errEncoded = '';
      try {
        errEncoded = stringifyError(this.err);
      } catch (err) {
        console.warn('failed to encode error reporting');
      }

      const reportObj = {
        ts,
        user,
        route,
        errEncoded,
      };

      try {
        await Post(urls.REPORT_ERROR, {
          error_details: reportObj,
          message: this.message,
        });
      } catch (err) {
        console.warn('failed to send error reporting');
      }

      this.$emit('clearError');
      this.loading = false;
    },
  },

  mounted() {},

  props: {
    // Objects
    err: {
      type: Error,
      required: true,
    },
  },

  watch: {
    // Error handling watcher.
    err: {
      handler: function handle(e) {
        if (!e) return;
        if (process.env.NODE_ENV === 'development') {
          console.debug(stringifyError(this.err, '\t'));
        }
        if (
          e.message === AuthErrorString ||
          _.includes(e.message, AuthErrorTokenExpString) ||
          _.includes(e.name, AuthErrorTokenExpString) ||
          _.includes(e.name, AuthErrorTokenExpUnknownString)
        ) {
          this.signOut({ alreadyLoggedOut: true });
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  text-align: center;
  font-family: helvetica;
  font-weight: lighter;
  padding: 0;
  margin: 0;
  font-size: 3em;
  margin-top: 10px;
}
p {
  text-align: center;
  font-family: helvetica;
  color: #aaa;
  font-size: 12px;
}
.round-corners {
  border-radius: 25px;
  padding: 20px;
}
.window {
  position: relative;
  margin: 0 auto;
  margin-top: 15px;
  height: 150px;
  width: 200px;
  background: #fff;
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  border-radius: 5px;
  -webkit-box-shadow: 0 3px 5px 1px rgba(0, 0, 0, 0.1);
  box-shadow: 0 3px 5px 1px rgba(0, 0, 0, 0.1);
}
.window::before {
  content: '';
  position: absolute;
  width: 100px;
  height: 150px;
  background: rgba(230, 230, 230, 0.3);
  z-index: 2;
  margin-left: 100px;
  -webkit-border-top-right-radius: 5px;
  -webkit-border-bottom-right-radius: 5px;
  -moz-border-radius-topright: 5px;
  -moz-border-radius-bottomright: 5px;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
}
.window::after {
  content: '';
  position: absolute;
  width: 200px;
  height: 20px;
  background: #e3e3e3;
  -webkit-border-top-left-radius: 5px;
  -webkit-border-top-right-radius: 5px;
  -moz-border-radius-topleft: 5px;
  -moz-border-radius-topright: 5px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}
.window .button {
  height: 7px;
  width: 7px;
  position: absolute;
  background: #a60303;
  z-index: 2;
  -webkit-border-radius: 10px;
  border-radius: 10px;
  margin-top: 6px;
  margin-left: 10px;
}
.window .button::before {
  content: '';
  height: 7px;
  width: 7px;
  position: absolute;
  background: #f2cb05;
  z-index: 2;
  -webkit-border-radius: 10px;
  border-radius: 10px;
  margin-left: 10px;
}
.window .button::after {
  content: '';
  height: 7px;
  width: 7px;
  position: absolute;
  background: #098429;
  z-index: 2;
  -webkit-border-radius: 10px;
  border-radius: 10px;
  margin-left: 20px;
}
.window .display {
  position: absolute;
  color: #a60303;
  z-index: 2;
  font-family: helvetica;
  font-size: 75px;
  margin-left: 78px;
  margin-top: 80px;
  font-weight: bold;
}
.window .display::before {
  content: '{';
  position: absolute;
  color: #c3c3c3;
  margin-left: -50px;
  font-size: 90px;
  margin-top: -10px;
}
.window .display::after {
  content: '}';
  position: absolute;
  color: #c3c3c3;
  margin-left: 15px;
  font-size: 90px;
  margin-top: -10px;
}

.error-form {
  width: 70vw;
  margin: 0 auto;
}
@media only screen and (max-width: 840px) {
  .error-form {
    width: 100%;
  }
}
</style>
